/**
 * DemoRequestForm — the shared contact/demo form body (fields + validation + POST +
 * success state), with NO surrounding chrome of its own.
 *
 * Extracted from DemoRequestDialog so the SAME implementation backs both surfaces:
 *   - DemoRequestDialog — wraps this in the modal overlay/panel (landing CTAs).
 *   - ScheduleDemoPage  — renders this inline as a full standalone /schedule-demo page.
 * Keeping one copy means the public-contact POST + validation never drift between the
 * popup and the page.
 *
 * Rendered inside the `.retroui-root` ancestor, so brand Tailwind tokens + the scoped
 * Preflight-off reset apply. Because Preflight is OFF, every border here is written
 * explicitly as `border-solid` — otherwise the border style is `none` and the hard
 * brutalist outline is invisible.
 *
 * Behaviour:
 *   - Name + Work email are required (email must contain "@"); inline error otherwise.
 *   - On submit we POST to the public contact endpoint, which sends an email to
 *     support@studysyncs.com server-side, then swap the form for a success state.
 *     A failed request keeps the form open with an inline error (NO false success).
 *   - Field state is local; the component remounts fresh per use (the dialog returns
 *     null when closed, so reopening gives a clean form), so there is no reset effect.
 */
import React from 'react';
import { Check } from 'lucide-react';
import { Input } from '../retroui';

/** Which CTA opened the form — drives copy here and the email subject server-side. */
export type ContactSource = 'demo' | 'talk-to-us';

export interface DemoRequestFormProps {
  /** Defaults to 'demo' (the "Schedule a demo" CTA / page). */
  source?: ContactSource;
  /** Render the form-state title + subtitle. The full page supplies its own hero
   *  heading, so it turns this OFF; the dialog leaves it ON. */
  showHeading?: boolean;
  /** Focus the first field on mount — the dialog wants this; the full page does not
   *  (auto-focus would scroll the page down past its hero on load). */
  autoFocus?: boolean;
  /** Success-state primary action. The dialog passes its close handler (button labelled
   *  "Done"); the page omits it and the form falls back to a "Back to home" link. */
  onDone?: () => void;
}

/** Per-source copy. Keeps the single form feeling purpose-built for each entry point. */
const SOURCE_COPY: Record<ContactSource, { title: string; subtitle: string; submit: string }> = {
  'demo': {
    title: 'Schedule a demo',
    subtitle: 'Tell us about your study and we’ll set up a walkthrough.',
    submit: 'Send request',
  },
  'talk-to-us': {
    title: 'Talk to us about your study',
    subtitle: 'Tell us what you’re measuring and we’ll get back to you.',
    submit: 'Send message',
  },
};

// Same default the public-stats hook uses (newbackend on :3002 in dev).
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
const CONTACT_ENDPOINT = `${API_BASE}/api/public/contact`;

/** Shared brutalist field label: small, uppercase, bold, letter-spaced. */
const labelClass = 'font-sans font-bold text-xs tracking-wide uppercase text-foreground';

/** Brutalist override for the RetroUI <Input>: square corners + hard black solid border. */
const inputClass = 'rounded-none border-solid border-black';

const DemoRequestForm: React.FC<DemoRequestFormProps> = ({
  source = 'demo',
  showHeading = true,
  autoFocus = false,
  onDone,
}) => {
  const copy = SOURCE_COPY[source] ?? SOURCE_COPY.demo;

  // ---- controlled form state -------------------------------------------------
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [org, setOrg] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  // Focus the first field on mount when asked (dialog only). RetroUI's <Input> is a
  // plain function component (no forwarded ref), so focus by id rather than via a ref.
  React.useEffect(() => {
    if (!autoFocus) return;
    const id = window.requestAnimationFrame(() => {
      const first = document.getElementById('demo-name') as HTMLInputElement | null;
      first?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [autoFocus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    // Validation: Name + Work email required; email must contain "@".
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) {
      setError('Please enter your name.');
      return;
    }
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setError('Please enter a valid work email.');
      return;
    }
    setError(null);
    setSubmitting(true);

    // POST to the public contact endpoint, which sends the email server-side. We only
    // flip to the success state on a 2xx — a failure keeps the form open with an
    // inline error so we never claim a request was sent when it wasn't.
    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          organization: org.trim(),
          message: message.trim(),
          source,
        }),
      });
      if (!res.ok) {
        throw new Error(`contact responded ${res.status}`);
      }
      setSent(true);
    } catch {
      setError(
        'Something went wrong sending your request. Please email support@studysyncs.com directly.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    // ---- SUCCESS STATE (replaces the form after a confirmed send) --------------
    return (
      <div className="flex flex-col items-center py-4 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-none border-2 border-solid border-black bg-green">
          <Check className="h-7 w-7 text-white" strokeWidth={3} aria-hidden="true" />
        </div>
        <h2 className="font-head text-2xl uppercase text-foreground">Request sent</h2>
        <p className="mt-3 font-sans text-sm text-muted-foreground">
          Thanks &mdash; your request is on its way to our team.
        </p>
        <p className="mt-1 font-sans text-sm text-muted-foreground">
          We&rsquo;ll reply from{' '}
          <span className="font-bold text-foreground">support@studysyncs.com</span>.
        </p>
        {onDone ? (
          <button
            type="button"
            onClick={onDone}
            className="mt-6 w-full cursor-pointer rounded-none border-2 border-solid border-black bg-primary px-6 py-3 font-sans font-bold uppercase tracking-[0.15em] text-primary-foreground transition hover:bg-primary-hover"
          >
            Done
          </button>
        ) : (
          <a
            href="/"
            className="mt-6 inline-block w-full rounded-none border-2 border-solid border-black bg-primary px-6 py-3 font-sans font-bold uppercase tracking-[0.15em] text-primary-foreground no-underline transition hover:bg-primary-hover"
          >
            Back to home
          </a>
        )}
      </div>
    );
  }

  // ---- FORM STATE --------------------------------------------------------------
  return (
    <>
      {showHeading && (
        <>
          <h2 className="font-head text-2xl uppercase text-foreground md:text-3xl">
            {copy.title}
          </h2>
          <p className="mt-2 font-sans text-sm text-muted-foreground md:text-base">
            {copy.subtitle}
          </p>
        </>
      )}

      <form
        className={`flex flex-col gap-5 ${showHeading ? 'mt-8' : ''}`}
        onSubmit={handleSubmit}
        noValidate
      >
        {/* Name + Work email side-by-side on wider panels (stack on mobile). */}
        <div className="flex flex-col gap-5 sm:flex-row">
          {/* Name (required) */}
          <div className="flex flex-1 flex-col gap-1.5">
            <label htmlFor="demo-name" className={labelClass}>
              Name
            </label>
            <Input
              id="demo-name"
              type="text"
              placeholder="Jane Researcher"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              aria-required="true"
              className={inputClass}
            />
          </div>

          {/* Work email (required) */}
          <div className="flex flex-1 flex-col gap-1.5">
            <label htmlFor="demo-email" className={labelClass}>
              Work email
            </label>
            <Input
              id="demo-email"
              type="email"
              placeholder="jane@university.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-required="true"
              className={inputClass}
            />
          </div>
        </div>

        {/* Organization (optional) */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="demo-org" className={labelClass}>
            Organization
          </label>
          <Input
            id="demo-org"
            type="text"
            placeholder="Stanford Medicine"
            value={org}
            onChange={(e) => setOrg(e.target.value)}
            className={inputClass}
          />
        </div>

        {/* Message (optional) — styled textarea matching the Input look. */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="demo-message" className={labelClass}>
            Tell us about your study
          </label>
          <textarea
            id="demo-message"
            rows={5}
            placeholder="What are you measuring, and who are your participants?"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full rounded-none border-2 border-solid border-black bg-white px-3 py-2 font-sans text-sm text-foreground transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          />
        </div>

        {/* Inline validation / submit error. */}
        {error && (
          <p role="alert" className="font-sans text-sm font-bold text-destructive">
            {error}
          </p>
        )}

        {/* Submit (full-width Cardinal primary). Disabled + relabelled while sending. */}
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 w-full cursor-pointer rounded-none border-2 border-solid border-black bg-primary px-6 py-3 font-sans font-bold uppercase tracking-[0.15em] text-primary-foreground transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Sending…' : copy.submit}
        </button>
      </form>
    </>
  );
};

export default DemoRequestForm;
